/**
 * Command Palette Component - Phase 5.5
 *
 * A powerful command palette for quick navigation and actions
 * Triggered with Cmd/Ctrl + K
 *
 * Features:
 * - Fuzzy search across all pages and actions
 * - Recently visited pages prioritized
 * - Quick actions (Create Invoice, Record Payment, etc.)
 * - Keyboard navigation
 * - Visual keyboard shortcuts hints
 */

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Drawer, Input, List, Typography, Space, Tag, theme } from 'antd';

import {
  DashboardOutlined,
  FileTextOutlined,
  DollarOutlined,
  ShoppingOutlined,
  AppstoreOutlined,
  TeamOutlined,
  SettingOutlined,
  BarChartOutlined,
  PlusOutlined,
  DatabaseOutlined,
  BookOutlined,
  ShoppingCartOutlined,
  CarOutlined,
  ToolOutlined,
  BuildOutlined,
  FundOutlined,
  ClockCircleOutlined,
  CalculatorOutlined,
  LineChartOutlined,
  AccountBookOutlined,
  TransactionOutlined,
  CalendarOutlined,
  HistoryOutlined,
  ApiOutlined,
  KeyOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  FileDoneOutlined,
  CheckOutlined,
  InboxOutlined,
  SearchOutlined,
  GlobalOutlined,
  SolutionOutlined,
  ProjectOutlined,
  FolderOutlined,
  ScanOutlined,
  UserOutlined,
  WalletOutlined,
  ReconciliationOutlined,
  SwapOutlined,
  CopyOutlined,
  AuditOutlined,
  CloudServerOutlined,
  BarcodeOutlined,
  EnvironmentOutlined,
  EditOutlined,
  BulbOutlined,
  CompassOutlined,
} from '@ant-design/icons';
import useNavigationStore from '../../store/navigationStore';
import useThemeStore from '../../store/themeStore';
import './CommandPalette.css';

const { Text } = Typography;
const { useToken } = theme;

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  category: 'navigation' | 'action' | 'recent';
  keywords?: string[];
}

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { recentItems } = useNavigationStore();
  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';
  const { token } = useToken();

  // Navigation commands
  const navigationCommands: CommandItem[] = [
    {
      id: 'nav-dashboard',
      label: 'Dashboard',
      description: 'View system overview and metrics',
      icon: <DashboardOutlined />,
      action: () => { navigate('/'); onClose(); },
      category: 'navigation',
      keywords: ['home', 'overview', 'metrics'],
    },
    {
      id: 'nav-invoices',
      label: 'Invoices',
      description: 'View and manage invoices',
      icon: <FileTextOutlined />,
      action: () => { navigate('/invoices'); onClose(); },
      category: 'navigation',
      keywords: ['billing', 'ar', 'receivables'],
    },
    {
      id: 'nav-payments',
      label: 'Payments',
      description: 'Record and track payments',
      icon: <DollarOutlined />,
      action: () => { navigate('/payments?action=create'); onClose(); },
      category: 'navigation',
      keywords: ['money', 'cash', 'receipts'],
    },
    {
      id: 'nav-expenses',
      label: 'Expenses',
      description: 'Manage business expenses',
      icon: <ShoppingOutlined />,
      action: () => { navigate('/expenses'); onClose(); },
      category: 'navigation',
      keywords: ['ap', 'payables', 'costs'],
    },
    {
      id: 'nav-coa',
      label: 'Chart of Accounts',
      description: 'Manage account structure',
      icon: <DatabaseOutlined />,
      action: () => { navigate('/accounting/chart-of-accounts'); onClose(); },
      category: 'navigation',
      keywords: ['accounts', 'ledger', 'gl'],
    },
    {
      id: 'nav-journal',
      label: 'Journal Entries',
      description: 'View and create journal entries',
      icon: <BookOutlined />,
      action: () => { navigate('/accounting/journal-entries'); onClose(); },
      category: 'navigation',
      keywords: ['transactions', 'ledger', 'entries'],
    },
    {
      id: 'nav-inventory',
      label: 'Inventory Items',
      description: 'Manage inventory items',
      icon: <AppstoreOutlined />,
      action: () => { navigate('/inventory/items'); onClose(); },
      category: 'navigation',
      keywords: ['stock', 'products', 'items'],
    },
    {
      id: 'nav-inventory-dashboard',
      label: 'Inventory Dashboard',
      description: 'View inventory metrics',
      icon: <BarChartOutlined />,
      action: () => { navigate('/inventory/dashboard'); onClose(); },
      category: 'navigation',
      keywords: ['stock', 'metrics', 'overview'],
    },
    {
      id: 'nav-po',
      label: 'Purchase Orders',
      description: 'Manage purchase orders',
      icon: <ShoppingCartOutlined />,
      action: () => { navigate('/purchase-orders'); onClose(); },
      category: 'navigation',
      keywords: ['po', 'buying', 'procurement'],
    },
    {
      id: 'nav-so',
      label: 'Sales Orders',
      description: 'Manage sales orders',
      icon: <CarOutlined />,
      action: () => { navigate('/sales-orders'); onClose(); },
      category: 'navigation',
      keywords: ['so', 'selling', 'orders'],
    },
    {
      id: 'nav-bom',
      label: 'Bill of Materials',
      description: 'Manage BOMs and recipes',
      icon: <BuildOutlined />,
      action: () => { navigate('/bom'); onClose(); },
      category: 'navigation',
      keywords: ['bom', 'manufacturing', 'assembly'],
    },
    {
      id: 'nav-assembly',
      label: 'Assembly Orders',
      description: 'Manufacturing assembly orders',
      icon: <ToolOutlined />,
      action: () => { navigate('/assembly'); onClose(); },
      category: 'navigation',
      keywords: ['manufacturing', 'production', 'build'],
    },
    {
      id: 'nav-demand',
      label: 'Demand Planning',
      description: 'Forecasting and planning',
      icon: <FundOutlined />,
      action: () => { navigate('/demand-planning'); onClose(); },
      category: 'navigation',
      keywords: ['forecast', 'planning', 'mrp'],
    },
    {
      id: 'nav-reports',
      label: 'Reports',
      description: 'View financial reports',
      icon: <BarChartOutlined />,
      action: () => { navigate('/reports'); onClose(); },
      category: 'navigation',
      keywords: ['analytics', 'financial', 'statements'],
    },
    {
      id: 'nav-accounting-reports',
      label: 'Accounting Reports',
      description: 'Accounting reports overview',
      icon: <FileTextOutlined />,
      action: () => { navigate('/accounting/reports'); onClose(); },
      category: 'navigation',
      keywords: ['financial', 'reports', 'accounting'],
    },
    {
      id: 'nav-trial-balance',
      label: 'Trial Balance',
      description: 'Verify debits and credits',
      icon: <CalculatorOutlined />,
      action: () => { navigate('/reports/trial-balance'); onClose(); },
      category: 'navigation',
      keywords: ['trial', 'balance', 'accounting', 'report', 'gl', 'ledger', 'debits', 'credits'],
    },
    {
      id: 'nav-income-statement',
      label: 'Income Statement',
      description: 'Profit & Loss statement',
      icon: <LineChartOutlined />,
      action: () => { navigate('/reports/income-statement'); onClose(); },
      category: 'navigation',
      keywords: ['income', 'p&l', 'profit', 'loss', 'pnl', 'statement', 'revenue', 'expenses'],
    },
    {
      id: 'nav-balance-sheet',
      label: 'Balance Sheet',
      description: 'Financial position statement',
      icon: <AccountBookOutlined />,
      action: () => { navigate('/reports/balance-sheet'); onClose(); },
      category: 'navigation',
      keywords: ['balance', 'sheet', 'assets', 'liabilities', 'equity', 'financial', 'position'],
    },
    {
      id: 'nav-account-activity',
      label: 'Account Activity',
      description: 'Transaction history',
      icon: <TransactionOutlined />,
      action: () => { navigate('/reports/account-activity'); onClose(); },
      category: 'navigation',
      keywords: ['account', 'activity', 'ledger', 'transactions', 'gl', 'history'],
    },
    {
      id: 'nav-scheduled-reports',
      label: 'Scheduled Reports',
      description: 'Automated report delivery',
      icon: <CalendarOutlined />,
      action: () => { navigate('/reports/scheduled'); onClose(); },
      category: 'navigation',
      keywords: ['scheduled', 'automated', 'reports', 'recurring', 'email'],
    },
    {
      id: 'nav-report-history',
      label: 'Report History',
      description: 'Past report executions',
      icon: <HistoryOutlined />,
      action: () => { navigate('/reports/history'); onClose(); },
      category: 'navigation',
      keywords: ['history', 'reports', 'previous', 'past', 'generated', 'executions'],
    },
    {
      id: 'nav-users',
      label: 'Users',
      description: 'Manage user accounts',
      icon: <TeamOutlined />,
      action: () => { navigate('/users'); onClose(); },
      category: 'navigation',
      keywords: ['people', 'accounts', 'permissions'],
    },
    {
      id: 'nav-user-roles',
      label: 'Roles & Permissions',
      description: 'Configure user roles',
      icon: <SafetyCertificateOutlined />,
      action: () => { navigate('/users/roles'); onClose(); },
      category: 'navigation',
      keywords: ['roles', 'permissions', 'access', 'rbac', 'security'],
    },
    {
      id: 'nav-organizations',
      label: 'Organizations',
      description: 'Manage organizations',
      icon: <BankOutlined />,
      action: () => { navigate('/organizations'); onClose(); },
      category: 'navigation',
      keywords: ['orgs', 'tenants', 'multi-tenant', 'companies'],
    },
    {
      id: 'nav-po-approvals',
      label: 'PO Approvals',
      description: 'Approve purchase orders',
      icon: <CheckOutlined />,
      action: () => { navigate('/purchase-orders/approvals'); onClose(); },
      category: 'navigation',
      keywords: ['approve', 'po', 'purchase', 'orders', 'approval'],
    },
    {
      id: 'nav-po-receiving',
      label: 'Receiving',
      description: 'Receive goods',
      icon: <InboxOutlined />,
      action: () => { navigate('/purchase-orders/receiving'); onClose(); },
      category: 'navigation',
      keywords: ['receiving', 'goods', 'receipt', 'inventory'],
    },
    {
      id: 'nav-so-fulfillment',
      label: 'Fulfillment Tracking',
      description: 'Track order fulfillment',
      icon: <CheckOutlined />,
      action: () => { navigate('/sales-orders/fulfillment'); onClose(); },
      category: 'navigation',
      keywords: ['fulfillment', 'orders', 'picking', 'packing'],
    },
    {
      id: 'nav-so-shipping',
      label: 'Shipping',
      description: 'Manage shipments',
      icon: <CarOutlined />,
      action: () => { navigate('/sales-orders/shipping'); onClose(); },
      category: 'navigation',
      keywords: ['shipping', 'delivery', 'freight', 'carrier'],
    },
    {
      id: 'nav-reorder-points',
      label: 'Reorder Points',
      description: 'Configure reorder points',
      icon: <CalculatorOutlined />,
      action: () => { navigate('/demand-planning/reorder-points'); onClose(); },
      category: 'navigation',
      keywords: ['reorder', 'points', 'min', 'max', 'safety', 'stock'],
    },
    {
      id: 'nav-safety-stock',
      label: 'Safety Stock',
      description: 'Safety stock calculator',
      icon: <SafetyOutlined />,
      action: () => { navigate('/demand-planning/safety-stock'); onClose(); },
      category: 'navigation',
      keywords: ['safety', 'stock', 'buffer', 'inventory'],
    },
    {
      id: 'nav-requisitions',
      label: 'Purchase Requisitions',
      description: 'View purchase requisitions',
      icon: <FileDoneOutlined />,
      action: () => { navigate('/demand-planning/requisitions'); onClose(); },
      category: 'navigation',
      keywords: ['requisitions', 'requests', 'purchase', 'pr'],
    },
    {
      id: 'nav-requisition-approval',
      label: 'Requisition Approvals',
      description: 'Approve requisitions',
      icon: <CheckOutlined />,
      action: () => { navigate('/demand-planning/requisitions/approval'); onClose(); },
      category: 'navigation',
      keywords: ['approve', 'requisitions', 'approval', 'pr'],
    },
    {
      id: 'nav-api-management',
      label: 'API Management',
      description: 'API management dashboard',
      icon: <ApiOutlined />,
      action: () => { navigate('/api-management'); onClose(); },
      category: 'navigation',
      keywords: ['api', 'rest', 'integration', 'developer'],
    },
    {
      id: 'nav-api-keys',
      label: 'API Keys',
      description: 'Manage API keys',
      icon: <KeyOutlined />,
      action: () => { navigate('/api-management/keys'); onClose(); },
      category: 'navigation',
      keywords: ['api', 'keys', 'tokens', 'authentication', 'credentials'],
    },
    {
      id: 'nav-rate-limits',
      label: 'Rate Limits',
      description: 'Configure rate limiting',
      icon: <ThunderboltOutlined />,
      action: () => { navigate('/api-management/rate-limits'); onClose(); },
      category: 'navigation',
      keywords: ['rate', 'limits', 'throttle', 'quota', 'api'],
    },
    {
      id: 'nav-webhooks',
      label: 'Webhooks',
      description: 'Webhook configuration',
      icon: <LinkOutlined />,
      action: () => { navigate('/api-management/webhooks'); onClose(); },
      category: 'navigation',
      keywords: ['webhooks', 'callbacks', 'events', 'integration'],
    },
    {
      id: 'nav-customers',
      label: 'Customers',
      description: 'Manage customers',
      icon: <UserOutlined />,
      action: () => { navigate('/customers'); onClose(); },
      category: 'navigation',
      keywords: ['customers', 'clients', 'buyers'],
    },
    {
      id: 'nav-vendors',
      label: 'Vendors',
      description: 'Manage vendors',
      icon: <ShoppingCartOutlined />,
      action: () => { navigate('/vendors'); onClose(); },
      category: 'navigation',
      keywords: ['vendors', 'suppliers', 'sellers'],
    },
    {
      id: 'nav-quotes',
      label: 'Quotes',
      description: 'Sales quotes',
      icon: <FileTextOutlined />,
      action: () => { navigate('/quotes'); onClose(); },
      category: 'navigation',
      keywords: ['quotes', 'proposals', 'estimates'],
    },
    {
      id: 'nav-rmas',
      label: 'RMA (Returns)',
      description: 'Return merchandise authorizations',
      icon: <SwapOutlined />,
      action: () => { navigate('/rmas'); onClose(); },
      category: 'navigation',
      keywords: ['rma', 'returns', 'refunds'],
    },
    {
      id: 'nav-banking',
      label: 'Bank Accounts',
      description: 'Manage bank accounts',
      icon: <BankOutlined />,
      action: () => { navigate('/banking/accounts'); onClose(); },
      category: 'navigation',
      keywords: ['banking', 'accounts', 'cash'],
    },
    {
      id: 'nav-bank-reconciliation',
      label: 'Bank Reconciliation',
      description: 'Reconcile bank statements',
      icon: <ReconciliationOutlined />,
      action: () => { navigate('/banking/reconciliation'); onClose(); },
      category: 'navigation',
      keywords: ['reconciliation', 'banking', 'statements'],
    },
    {
      id: 'nav-bank-transfers',
      label: 'Bank Transfers',
      description: 'Transfer between accounts',
      icon: <SwapOutlined />,
      action: () => { navigate('/banking/transfers'); onClose(); },
      category: 'navigation',
      keywords: ['transfers', 'banking', 'move'],
    },
    {
      id: 'nav-cash-ledger',
      label: 'Cash Ledger',
      description: 'View cash transactions',
      icon: <WalletOutlined />,
      action: () => { navigate('/transactions/cash-ledger'); onClose(); },
      category: 'navigation',
      keywords: ['cash', 'ledger', 'transactions'],
    },
    {
      id: 'nav-stock-movements',
      label: 'Stock Movements',
      description: 'Inventory movements',
      icon: <SwapOutlined />,
      action: () => { navigate('/inventory/movements'); onClose(); },
      category: 'navigation',
      keywords: ['movements', 'inventory', 'transfers'],
    },
    {
      id: 'nav-stock-adjustments',
      label: 'Stock Adjustments',
      description: 'Adjust inventory levels',
      icon: <EditOutlined />,
      action: () => { navigate('/inventory/adjustments'); onClose(); },
      category: 'navigation',
      keywords: ['adjustments', 'inventory', 'stock'],
    },
    {
      id: 'nav-cycle-count',
      label: 'Cycle Count',
      description: 'Physical inventory counting',
      icon: <CheckOutlined />,
      action: () => { navigate('/inventory/cycle-count'); onClose(); },
      category: 'navigation',
      keywords: ['cycle', 'count', 'inventory', 'physical'],
    },
    {
      id: 'nav-serial-numbers',
      label: 'Serial Numbers',
      description: 'Serial number tracking',
      icon: <BarcodeOutlined />,
      action: () => { navigate('/inventory/serial-numbers'); onClose(); },
      category: 'navigation',
      keywords: ['serial', 'numbers', 'tracking'],
    },
    {
      id: 'nav-locations',
      label: 'Locations',
      description: 'Warehouse locations',
      icon: <EnvironmentOutlined />,
      action: () => { navigate('/inventory/locations'); onClose(); },
      category: 'navigation',
      keywords: ['locations', 'warehouse', 'bins'],
    },
    {
      id: 'nav-work-orders',
      label: 'Work Orders',
      description: 'Manufacturing work orders',
      icon: <ToolOutlined />,
      action: () => { navigate('/manufacturing/work-orders'); onClose(); },
      category: 'navigation',
      keywords: ['work', 'orders', 'manufacturing', 'production'],
    },
    {
      id: 'nav-bom-cost',
      label: 'BOM Cost Analysis',
      description: 'Bill of materials costing',
      icon: <CalculatorOutlined />,
      action: () => { navigate('/bom/cost-analysis'); onClose(); },
      category: 'navigation',
      keywords: ['bom', 'cost', 'analysis', 'costing'],
    },
    {
      id: 'nav-forecast',
      label: 'Demand Forecasting',
      description: 'Create and view forecasts',
      icon: <LineChartOutlined />,
      action: () => { navigate('/demand-planning/forecast'); onClose(); },
      category: 'navigation',
      keywords: ['forecast', 'demand', 'planning'],
    },
    {
      id: 'nav-suggestions',
      label: 'Reorder Suggestions',
      description: 'View reorder suggestions',
      icon: <BulbOutlined />,
      action: () => { navigate('/demand-planning/suggestions'); onClose(); },
      category: 'navigation',
      keywords: ['suggestions', 'reorder', 'planning'],
    },
    {
      id: 'nav-cash-flow',
      label: 'Cash Flow Report',
      description: 'Cash flow statement',
      icon: <DollarOutlined />,
      action: () => { navigate('/reports/cash-flow'); onClose(); },
      category: 'navigation',
      keywords: ['cash', 'flow', 'report', 'statement'],
    },
    {
      id: 'nav-aging',
      label: 'Aging Reports',
      description: 'AR/AP aging',
      icon: <ClockCircleOutlined />,
      action: () => { navigate('/reports/aging'); onClose(); },
      category: 'navigation',
      keywords: ['aging', 'ar', 'ap', 'receivables', 'payables'],
    },
    {
      id: 'nav-inventory-reports',
      label: 'Inventory Reports',
      description: 'Stock and valuation reports',
      icon: <BarChartOutlined />,
      action: () => { navigate('/reports/inventory'); onClose(); },
      category: 'navigation',
      keywords: ['inventory', 'reports', 'stock', 'valuation'],
    },
    {
      id: 'nav-report-builder',
      label: 'Custom Report Builder',
      description: 'Build custom reports',
      icon: <BuildOutlined />,
      action: () => { navigate('/reports/builder'); onClose(); },
      category: 'navigation',
      keywords: ['custom', 'builder', 'reports'],
    },
    {
      id: 'nav-payroll',
      label: 'Employees',
      description: 'Manage employees',
      icon: <SolutionOutlined />,
      action: () => { navigate('/payroll/employees'); onClose(); },
      category: 'navigation',
      keywords: ['payroll', 'employees', 'staff'],
    },
    {
      id: 'nav-payroll-runs',
      label: 'Payroll Runs',
      description: 'Process payroll',
      icon: <CalendarOutlined />,
      action: () => { navigate('/payroll/payroll-runs'); onClose(); },
      category: 'navigation',
      keywords: ['payroll', 'runs', 'processing'],
    },
    {
      id: 'nav-timesheet-entry',
      label: 'Timesheet Entry',
      description: 'Enter timesheets',
      icon: <ClockCircleOutlined />,
      action: () => { navigate('/payroll/timesheet-entry'); onClose(); },
      category: 'navigation',
      keywords: ['timesheet', 'entry', 'hours'],
    },
    {
      id: 'nav-timesheet-approval',
      label: 'Timesheet Approval',
      description: 'Approve timesheets',
      icon: <CheckOutlined />,
      action: () => { navigate('/payroll/timesheet-approval'); onClose(); },
      category: 'navigation',
      keywords: ['timesheet', 'approval', 'approve'],
    },
    {
      id: 'nav-tax-setup',
      label: 'Tax Setup',
      description: 'Configure payroll taxes',
      icon: <CalculatorOutlined />,
      action: () => { navigate('/payroll/tax-setup'); onClose(); },
      category: 'navigation',
      keywords: ['tax', 'setup', 'payroll'],
    },
    {
      id: 'nav-pay-stubs',
      label: 'Pay Stubs',
      description: 'View pay stubs',
      icon: <FileTextOutlined />,
      action: () => { navigate('/payroll/pay-stubs'); onClose(); },
      category: 'navigation',
      keywords: ['pay', 'stubs', 'payroll'],
    },
    {
      id: 'nav-payroll-reports',
      label: 'Payroll Reports',
      description: 'Payroll reporting',
      icon: <BarChartOutlined />,
      action: () => { navigate('/payroll/reports'); onClose(); },
      category: 'navigation',
      keywords: ['payroll', 'reports'],
    },
    {
      id: 'nav-projects',
      label: 'Projects',
      description: 'Manage projects',
      icon: <ProjectOutlined />,
      action: () => { navigate('/projects'); onClose(); },
      category: 'navigation',
      keywords: ['projects', 'jobs'],
    },
    {
      id: 'nav-project-dashboard',
      label: 'Project Dashboard',
      description: 'Project overview',
      icon: <DashboardOutlined />,
      action: () => { navigate('/projects/dashboard'); onClose(); },
      category: 'navigation',
      keywords: ['project', 'dashboard', 'overview'],
    },
    {
      id: 'nav-time-entries',
      label: 'Time Tracking',
      description: 'Track project time',
      icon: <ClockCircleOutlined />,
      action: () => { navigate('/projects/time-entries'); onClose(); },
      category: 'navigation',
      keywords: ['time', 'tracking', 'entries'],
    },
    {
      id: 'nav-documents',
      label: 'Documents',
      description: 'Document repository',
      icon: <FolderOutlined />,
      action: () => { navigate('/documents'); onClose(); },
      category: 'navigation',
      keywords: ['documents', 'files', 'repository'],
    },
    {
      id: 'nav-receipts',
      label: 'Receipts',
      description: 'Manage receipts',
      icon: <FileTextOutlined />,
      action: () => { navigate('/receipts'); onClose(); },
      category: 'navigation',
      keywords: ['receipts', 'documents'],
    },
    {
      id: 'nav-receipt-scanner',
      label: 'Receipt Scanner',
      description: 'Scan receipts',
      icon: <ScanOutlined />,
      action: () => { navigate('/receipts/scanner'); onClose(); },
      category: 'navigation',
      keywords: ['scanner', 'receipts', 'ocr'],
    },
    {
      id: 'nav-templates',
      label: 'Document Templates',
      description: 'Manage templates',
      icon: <CopyOutlined />,
      action: () => { navigate('/documents/templates'); onClose(); },
      category: 'navigation',
      keywords: ['templates', 'documents'],
    },
    {
      id: 'nav-budgets',
      label: 'Budgets',
      description: 'Manage budgets',
      icon: <WalletOutlined />,
      action: () => { navigate('/budgets'); onClose(); },
      category: 'navigation',
      keywords: ['budgets', 'planning'],
    },
    {
      id: 'nav-budget-analysis',
      label: 'Budget Analysis',
      description: 'Analyze budget performance',
      icon: <LineChartOutlined />,
      action: () => { navigate('/budgets/analysis'); onClose(); },
      category: 'navigation',
      keywords: ['budget', 'analysis', 'variance'],
    },
    {
      id: 'nav-assets',
      label: 'Fixed Assets',
      description: 'Manage fixed assets',
      icon: <BankOutlined />,
      action: () => { navigate('/assets'); onClose(); },
      category: 'navigation',
      keywords: ['assets', 'fixed', 'depreciation'],
    },
    {
      id: 'nav-depreciation',
      label: 'Depreciation Schedule',
      description: 'Asset depreciation',
      icon: <CalendarOutlined />,
      action: () => { navigate('/assets/depreciation'); onClose(); },
      category: 'navigation',
      keywords: ['depreciation', 'schedule', 'assets'],
    },
    {
      id: 'nav-consolidated-dashboard',
      label: 'Consolidated Dashboard',
      description: 'Multi-company dashboard',
      icon: <GlobalOutlined />,
      action: () => { navigate('/dashboard/consolidated'); onClose(); },
      category: 'navigation',
      keywords: ['consolidated', 'multi', 'company'],
    },
    {
      id: 'nav-org-groups',
      label: 'Organization Groups',
      description: 'Manage organization groups',
      icon: <GlobalOutlined />,
      action: () => { navigate('/organizations/groups'); onClose(); },
      category: 'navigation',
      keywords: ['groups', 'organizations'],
    },
    {
      id: 'nav-consolidation',
      label: 'Consolidation',
      description: 'Financial consolidation',
      icon: <CopyOutlined />,
      action: () => { navigate('/organizations/consolidation'); onClose(); },
      category: 'navigation',
      keywords: ['consolidation', 'financial'],
    },
    {
      id: 'nav-intercompany',
      label: 'Intercompany Transactions',
      description: 'Cross-company transactions',
      icon: <SwapOutlined />,
      action: () => { navigate('/organizations/intercompany'); onClose(); },
      category: 'navigation',
      keywords: ['intercompany', 'cross', 'company'],
    },
    {
      id: 'nav-org-comparison',
      label: 'Organization Comparison',
      description: 'Compare organizations',
      icon: <BarChartOutlined />,
      action: () => { navigate('/organizations/comparison'); onClose(); },
      category: 'navigation',
      keywords: ['comparison', 'organizations'],
    },
    {
      id: 'nav-group-cash-flow',
      label: 'Group Cash Flow',
      description: 'Consolidated cash flow',
      icon: <DollarOutlined />,
      action: () => { navigate('/organizations/cash-flow'); onClose(); },
      category: 'navigation',
      keywords: ['cash', 'flow', 'group'],
    },
    {
      id: 'nav-security',
      label: 'Security Settings',
      description: 'Security configuration',
      icon: <SafetyOutlined />,
      action: () => { navigate('/settings/security'); onClose(); },
      category: 'navigation',
      keywords: ['security', 'settings', '2fa'],
    },
    {
      id: 'nav-audit-logs',
      label: 'Audit Logs',
      description: 'System audit trail',
      icon: <AuditOutlined />,
      action: () => { navigate('/admin/audit-logs'); onClose(); },
      category: 'navigation',
      keywords: ['audit', 'logs', 'trail'],
    },
    {
      id: 'nav-multi-region',
      label: 'Multi-Region',
      description: 'Regional configuration',
      icon: <GlobalOutlined />,
      action: () => { navigate('/admin/multi-region'); onClose(); },
      category: 'navigation',
      keywords: ['multi', 'region', 'global'],
    },
    {
      id: 'nav-data-retention',
      label: 'Data Retention',
      description: 'Data retention policies',
      icon: <ClockCircleOutlined />,
      action: () => { navigate('/admin/data-retention'); onClose(); },
      category: 'navigation',
      keywords: ['data', 'retention', 'archival'],
    },
    {
      id: 'nav-cache',
      label: 'Cache Management',
      description: 'Manage system cache',
      icon: <CloudServerOutlined />,
      action: () => { navigate('/performance/cache'); onClose(); },
      category: 'navigation',
      keywords: ['cache', 'performance'],
    },
    {
      id: 'nav-archival',
      label: 'Data Archival',
      description: 'Archive old data',
      icon: <FolderOutlined />,
      action: () => { navigate('/performance/archival'); onClose(); },
      category: 'navigation',
      keywords: ['archival', 'archive', 'data'],
    },
    {
      id: 'nav-analytics',
      label: 'Usage Analytics',
      description: 'System usage metrics',
      icon: <BarChartOutlined />,
      action: () => { navigate('/performance/analytics'); onClose(); },
      category: 'navigation',
      keywords: ['usage', 'analytics', 'metrics'],
    },
    {
      id: 'nav-navigation-hub',
      label: 'Navigation Hub',
      description: 'Visual navigation center',
      icon: <CompassOutlined />,
      action: () => { navigate('/navigation'); onClose(); },
      category: 'navigation',
      keywords: ['navigation', 'hub', 'menu'],
    },
    {
      id: 'nav-settings',
      label: 'Settings',
      description: 'Application settings',
      icon: <SettingOutlined />,
      action: () => { navigate('/settings/general'); onClose(); },
      category: 'navigation',
      keywords: ['config', 'preferences', 'options'],
    },
  ];

  // Quick action commands
  const actionCommands: CommandItem[] = [
    {
      id: 'action-create-invoice',
      label: 'Create Invoice',
      description: 'Create a new invoice',
      icon: <PlusOutlined />,
      action: () => { navigate('/invoices?action=create'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'billing'],
    },
    {
      id: 'action-record-payment',
      label: 'Record Payment',
      description: 'Record a customer payment',
      icon: <PlusOutlined />,
      action: () => { navigate('/payments?action=create'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'money', 'cash'],
    },
    {
      id: 'action-new-journal',
      label: 'New Journal Entry',
      description: 'Create a journal entry',
      icon: <PlusOutlined />,
      action: () => { navigate('/accounting/journal-entries?action=create'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'transaction'],
    },
    {
      id: 'action-add-item',
      label: 'Add Inventory Item',
      description: 'Add a new inventory item',
      icon: <PlusOutlined />,
      action: () => { navigate('/inventory/items?action=create'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'product', 'stock'],
    },
    {
      id: 'action-create-po',
      label: 'Create Purchase Order',
      description: 'Create a new PO',
      icon: <PlusOutlined />,
      action: () => { navigate('/purchase-orders/new'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'buying'],
    },
    {
      id: 'action-create-so',
      label: 'Create Sales Order',
      description: 'Create a new SO',
      icon: <PlusOutlined />,
      action: () => { navigate('/sales-orders/new'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'selling'],
    },
    {
      id: 'action-create-expense',
      label: 'Create Expense',
      description: 'Record new expense',
      icon: <PlusOutlined />,
      action: () => { navigate('/expenses?action=create'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'expense', 'ap', 'payable'],
    },
    {
      id: 'action-create-org',
      label: 'Create Organization',
      description: 'Create new organization',
      icon: <PlusOutlined />,
      action: () => { navigate('/organizations?action=create'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'organization', 'tenant', 'company'],
    },
    {
      id: 'action-create-forecast',
      label: 'Create Forecast',
      description: 'Create demand forecast',
      icon: <PlusOutlined />,
      action: () => { navigate('/demand-planning/forecast/new'); onClose(); },
      category: 'action',
      keywords: ['new', 'add', 'forecast', 'demand', 'planning'],
    },
  ];

  // Recent items commands
  const recentCommands: CommandItem[] = useMemo(() => {
    return recentItems.slice(0, 5).map((item, index) => ({
      id: `recent-${index}`,
      label: item.label,
      description: 'Recently visited',
      icon: item.icon || <ClockCircleOutlined />,
      action: () => { navigate(item.key); onClose(); },
      category: 'recent' as const,
    }));
  }, [recentItems, navigate, onClose]);

  // All commands
  const allCommands = useMemo(() => {
    return [...recentCommands, ...navigationCommands, ...actionCommands];
  }, [recentCommands]);

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!search) return allCommands;

    const searchLower = search.toLowerCase();
    return allCommands.filter((cmd) => {
      // Search in label
      if (cmd.label.toLowerCase().includes(searchLower)) return true;

      // Search in description
      if (cmd.description?.toLowerCase().includes(searchLower)) return true;

      // Search in keywords
      if (cmd.keywords?.some((kw) => kw.toLowerCase().includes(searchLower))) return true;

      return false;
    });
  }, [allCommands, search]);

  // Group filtered commands by category
  const groupedCommands = useMemo(() => {
    const groups: Record<string, CommandItem[]> = {
      recent: [],
      action: [],
      navigation: [],
    };

    filteredCommands.forEach((cmd) => {
      groups[cmd.category].push(cmd);
    });

    return groups;
  }, [filteredCommands]);

  // Reset search when closed
  useEffect(() => {
    if (!open) {
      setSearch('');
    }
  }, [open]);

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case 'recent':
        return 'blue';
      case 'action':
        return 'green';
      case 'navigation':
        return 'default';
      default:
        return 'default';
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'recent':
        return 'Recent';
      case 'action':
        return 'Action';
      case 'navigation':
        return 'Navigate';
      default:
        return category;
    }
  };

  return (
    <Drawer
      title={
        <Space>
          <SearchOutlined style={{ fontSize: 18, color: '#1890ff' }} />
          <span>Search & Navigate</span>
        </Space>
      }
      placement="right"
      width={480}
      open={open}
      onClose={onClose}
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 55px)' },
      }}
    >
      {/* Search Input */}
      <div style={{
        padding: '16px',
        borderBottom: isDark ? '1px solid #303030' : '1px solid #f0f0f0',
        background: isDark ? '#141414' : '#fafafa'
      }}>
        <Input
          size="large"
          placeholder="Type to search pages, actions, or commands..."
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          allowClear
        />
      </div>

      {/* Results */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 0' }}>
        {filteredCommands.length === 0 ? (
          <div style={{ padding: '48px 24px', textAlign: 'center' }}>
            <Text type="secondary">No results found for "{search}"</Text>
          </div>
        ) : (
          <>
            {/* Recent Items */}
            {groupedCommands.recent.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  padding: '8px 24px',
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  opacity: 0.6,
                  color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
                }}>
                  Recent
                </div>
                <List
                  dataSource={groupedCommands.recent}
                  renderItem={(cmd) => (
                    <List.Item
                      key={cmd.id}
                      onClick={cmd.action}
                      style={{
                        padding: '12px 24px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = token.colorBgTextHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <List.Item.Meta
                        avatar={<div style={{ fontSize: 18 }}>{cmd.icon}</div>}
                        title={
                          <Space>
                            {cmd.label}
                            <Tag color={getCategoryBadgeColor(cmd.category)} style={{ fontSize: 10 }}>
                              {getCategoryLabel(cmd.category)}
                            </Tag>
                          </Space>
                        }
                        description={cmd.description}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Quick Actions */}
            {groupedCommands.action.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  padding: '8px 24px',
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  opacity: 0.6,
                  color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
                }}>
                  Actions
                </div>
                <List
                  dataSource={groupedCommands.action}
                  renderItem={(cmd) => (
                    <List.Item
                      key={cmd.id}
                      onClick={cmd.action}
                      style={{
                        padding: '12px 24px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = token.colorBgTextHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <List.Item.Meta
                        avatar={<div style={{ fontSize: 18, color: '#52c41a' }}>{cmd.icon}</div>}
                        title={
                          <Space>
                            {cmd.label}
                            <Tag color={getCategoryBadgeColor(cmd.category)} style={{ fontSize: 10 }}>
                              {getCategoryLabel(cmd.category)}
                            </Tag>
                          </Space>
                        }
                        description={cmd.description}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Navigation */}
            {groupedCommands.navigation.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{
                  padding: '8px 24px',
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: 'uppercase',
                  opacity: 0.6,
                  color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
                }}>
                  Navigation
                </div>
                <List
                  dataSource={groupedCommands.navigation}
                  renderItem={(cmd) => (
                    <List.Item
                      key={cmd.id}
                      onClick={cmd.action}
                      style={{
                        padding: '12px 24px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = token.colorBgTextHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <List.Item.Meta
                        avatar={<div style={{ fontSize: 18 }}>{cmd.icon}</div>}
                        title={
                          <Space>
                            {cmd.label}
                            <Tag color={getCategoryBadgeColor(cmd.category)} style={{ fontSize: 10 }}>
                              {getCategoryLabel(cmd.category)}
                            </Tag>
                          </Space>
                        }
                        description={cmd.description}
                      />
                    </List.Item>
                  )}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Footer with keyboard hints */}
      <div style={{
        padding: '16px',
        borderTop: isDark ? '1px solid #303030' : '1px solid #f0f0f0',
        background: isDark ? '#141414' : '#fafafa',
        display: 'flex',
        gap: 16,
        justifyContent: 'center',
      }}>
        <Space size="small">
          <Text type="secondary" style={{ fontSize: 12 }}>
            <kbd style={{
              padding: '2px 6px',
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadiusSM,
              backgroundColor: token.colorBgContainer,
              fontSize: 11
            }}>Ctrl</kbd> + <kbd style={{
              padding: '2px 6px',
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadiusSM,
              backgroundColor: token.colorBgContainer,
              fontSize: 11
            }}>K</kbd> to open
          </Text>
        </Space>
        <Space size="small">
          <Text type="secondary" style={{ fontSize: 12 }}>
            <kbd style={{
              padding: '2px 6px',
              border: `1px solid ${token.colorBorder}`,
              borderRadius: token.borderRadiusSM,
              backgroundColor: token.colorBgContainer,
              fontSize: 11
            }}>Esc</kbd> to close
          </Text>
        </Space>
      </div>
    </Drawer>
  );
}
